import { getSkinConfig } from "./skin-rules";
import { createChallengeLink } from "../config/challenge-link-config";
import {
  getModeConfig,
  type ModeId,
  type SkinId,
} from "../config/gameplay-config";
import type { DailyChallengeIdentity } from "../config/daily-challenge-config";
import type { SeasonalChallengeIdentity } from "../config/challenge-link-config";
import type { SharePayload } from "../config/share-config";

export interface ShareChallengeInput {
  readonly displayName: string;
  readonly identity: DailyChallengeIdentity | SeasonalChallengeIdentity;
  readonly kind: "daily" | "seasonal";
  readonly shareCallToAction?: string;
}

export interface ShareCardInput {
  readonly challenge?: ShareChallengeInput;
  readonly finalLevel: number;
  readonly isNewBestLevel: boolean;
  readonly mode: ModeId;
  readonly selectedSkinId: SkinId;
}

export interface ShareCardModel {
  readonly accessibleLabel: string;
  readonly brand: "Snakely";
  readonly challengeIdentity: string | null;
  readonly headline: string;
  readonly modeLabel: string;
  readonly resultTag: string;
  readonly selectedSkinId: SkinId;
  readonly share: SharePayload;
}

const appUrl = "https://snakely.jlong.dev/";

const createShareText = (
  input: ShareCardInput,
  headline: string,
  modeLabel: string,
): string => {
  if (input.challenge?.kind === "daily") {
    return `I reached ${headline} in today's Snakely Daily Challenge! Same challenge, same day. Can you beat it?`;
  }
  if (input.challenge?.kind === "seasonal") {
    return `I reached ${headline} in Snakely's ${modeLabel}! ${input.challenge.shareCallToAction ?? "Can you beat it?"}`;
  }
  const newBest = input.isNewBestLevel ? "New best! " : "";
  const mode = getModeConfig(input.mode);
  if (mode.roundDurationMs === null) {
    return `${newBest}I reached ${headline} in Snakely Free Play! Can you beat my snake?`;
  }
  const minutes = mode.roundDurationMs / 60_000;
  return `${newBest}I reached ${headline} in Snakely's ${String(minutes)} minute challenge! Can you beat my snake?`;
};

export const createShareCard = (input: ShareCardInput): ShareCardModel => {
  const headline = `Level ${String(input.finalLevel)}`;
  const challenge = input.challenge;
  const modeLabel =
    challenge?.displayName ?? getModeConfig(input.mode).displayName;
  const skin = getSkinConfig(input.selectedSkinId);
  const skinName = skin.displayName;
  const resultTag =
    challenge?.kind === "daily"
      ? "Daily complete!"
      : challenge?.kind === "seasonal"
        ? "Challenge complete!"
        : input.isNewBestLevel
          ? "New best!"
          : "Great run!";
  const resultLabel = input.isNewBestLevel ? " new best" : " result";
  const challengeLabel =
    challenge === undefined ? "" : ` Challenge ${challenge.identity}.`;
  const titleLabel =
    challenge === undefined
      ? input.isNewBestLevel
        ? "Snakely new best"
        : "Snakely result"
      : challenge.kind === "daily"
        ? "Snakely Daily Challenge"
        : `Snakely ${challenge.displayName}`;
  return {
    accessibleLabel: `Snakely${resultLabel}. ${headline} in ${modeLabel}. ${skinName} snake.${challengeLabel}`,
    brand: "Snakely",
    challengeIdentity: challenge?.identity ?? null,
    headline,
    modeLabel,
    resultTag,
    selectedSkinId: input.selectedSkinId,
    share: {
      dialogTitle: "Share your Snakely result",
      image: {
        accentColor: skin.accentColor,
        bodyColor: skin.bodyColor,
        headline,
        modeLabel,
        pattern: skin.pattern,
        resultTag,
        skinName,
      },
      nativeText: `Snakely result: ${headline} · ${modeLabel}.`,
      text: createShareText(input, headline, modeLabel),
      title: `${titleLabel} · ${headline}`,
      url:
        challenge === undefined
          ? appUrl
          : createChallengeLink(challenge.identity),
    },
  };
};
